import { Model } from '@contember/schema';
import { acceptEveryFieldVisitor, isOwningRelation, isRelation } from '@contember/schema-utils';
import { CREATED_AT_FIELD } from '../common';
import { dataTypesMapping, isEntityAccessType, isFieldAccessType, onDeleteMapping, relationTypeMap, stmtAclAllowEntity, stmtAclAllowFields, stmtAddEnumValue, stmtAddPlugin, stmtAddRelation, stmtAddSchemaColumn, stmtAddSchemaComment, stmtCreateEntity, stmtCreateRole, stmtOnDelete, stmtSetTranslatableColumn, stmtSetUnique, } from './stmts';
export class ModelStmtPrinter {
    print(schema) {
        const model = schema.contemberSchema.model;
        const extensions = schema.schemaExtensions;
        if (Object.keys(model?.entities ?? {}).length === 0) {
            return null;
        }
        const stmts = [
            ...this.printCustomEntitiesCreate(model),
            ...this.printCustomEnums(model),
            ...this.printCustomEntitiesFields(model, extensions),
            ...this.printCustomComments(extensions),
            ...this.printCustomPlugins(extensions),
            ...this.printCustomAcl(schema.contemberSchema),
            ...this.printCustomTranslatable(schema, extensions),
        ];
        if (stmts.length === 0) {
            return null;
        }
        return stmts.join('\n');
    }
    printCustomTranslatable(schema, extensions) {
        const stmts = [];
        for (const [entityName, fields] of Object.entries(extensions.translatable ?? {})) {
            for (const fieldName of fields) {
                const field = schema.contemberSchema.model.entities[entityName]?.fields[fieldName];
                if (!field) {
                    continue;
                }
                if (!isRelation(field)) {
                    stmts.push(stmtSetTranslatableColumn({ entityName, fieldName }));
                    continue;
                }
                // Inverse relation cannot be translatable
                if (isOwningRelation(field)) {
                    stmts.push(stmtSetTranslatableColumn({ entityName, fieldName }));
                }
            }
        }
        return stmts;
    }
    printCustomPlugins(extensions) {
        const stmts = [];
        for (const plugin of Object.values(extensions.plugins ?? {})) {
            for (const [entityName, { fields, params }] of Object.entries(plugin.connectedTo)) {
                if (fields?.length) {
                    for (const field of fields) {
                        stmts.push(stmtAddPlugin({ pluginName: plugin.name, entityName, fieldName: field, params }));
                    }
                    continue;
                }
                stmts.push(stmtAddPlugin({ pluginName: plugin.name, entityName, params }));
            }
        }
        return stmts;
    }
    printCustomComments(extensions) {
        const stmts = [];
        for (const [entityName, comments] of Object.entries(extensions.comments ?? {})) {
            for (const { comment } of comments) {
                stmts.push(stmtAddSchemaComment({ comment, entityName }));
            }
        }
        return stmts;
    }
    printCustomAcl(schema) {
        const stmts = [];
        const roles = Object.entries(schema.acl.roles);
        for (const [roleName, role] of roles) {
            stmts.push(stmtCreateRole({ roleName }));
            // TODO: implement roles inheritance
            // if (role.inherits) {
            // 	stmts.push(`inherits ${role.name} ${role.inherits.join(', ')}`)
            // }
            // TODO: implement ACL variables
            // for (const [name, value] of Object.entries(role.variables)) {
            // 	stmts.push(`add variable ${role.name} ${name} ${value.type} ${value.value}`)
            // }
            for (const [entityName, permissions] of Object.entries(role.entities)) {
                for (const [operationName, operation] of Object.entries(permissions.operations)) {
                    const accessType = operationName;
                    if (isEntityAccessType(accessType)) {
                        // Delete operation has only boolean value
                        if (operation === true) {
                            stmts.push(stmtAclAllowEntity({
                                roleNames: [roleName],
                                accessTypes: [accessType],
                                entityName,
                            }));
                            continue;
                        }
                        const entityFields = Object.keys(schema.model.entities[entityName].fields);
                        const fieldNames = Object.entries(operation ?? {})
                            .filter(([o, value]) => value === true || o)
                            .map(([key]) => key);
                        if (fieldNames.length === 0)
                            continue;
                        // Entity has one primary field that is not included in acl fields
                        if (isEntityAccessType(accessType) && fieldNames.length === entityFields.length - 1) {
                            stmts.push(stmtAclAllowEntity({
                                roleNames: [roleName],
                                accessTypes: [accessType],
                                entityName,
                            }));
                            continue;
                        }
                        if (isFieldAccessType(accessType)) {
                            stmts.push(stmtAclAllowFields({
                                roleNames: [roleName],
                                accessTypes: [accessType],
                                entityName,
                                fieldNames,
                            }));
                        }
                    }
                }
            }
        }
        return stmts;
    }
    pluginsConnectedTo(extensions) {
        const result = {};
        Object.values(extensions.plugins ?? {}).forEach(plugin => {
            Object.entries(plugin.connectedTo ?? {}).forEach(([entityName, { fields }]) => {
                result[entityName] = result?.[entityName] ?? [];
                for (const fieldName of fields ?? []) {
                    result[entityName][fieldName] = { fieldName, pluginName: plugin.name };
                }
            });
        });
        return result;
    }
    printCustomEntitiesCreate(model) {
        const stmts = [];
        for (const entity of Object.values(model.entities)) {
            stmts.push(...this.printCreateEntity(entity));
        }
        return stmts;
    }
    printCustomEntitiesFields(model, extensions) {
        const stmts = [];
        for (const entity of Object.values(model.entities)) {
            acceptEveryFieldVisitor(model, entity, {
                visitColumn: (context) => {
                    if (context.column.name === context.entity.primary || context.column.name === CREATED_AT_FIELD) {
                        return;
                    }
                    stmts.push(...this.printColumn(context));
                },
                visitRelation: (context) => {
                    const connectedTo = this.pluginsConnectedTo(extensions);
                    const pluginName = connectedTo[context.entity.name]?.[context.relation.name]?.pluginName;
                    if (pluginName)
                        return;
                    if (isOwningRelation(context.relation)) {
                        stmts.push(...this.printRelation(context));
                    }
                },
            });
            for (const { fields } of entity.unique) {
                stmts.push(stmtSetUnique({ entityName: entity.name, fields: [...fields] }));
            }
        }
        return stmts;
    }
    printCustomEnums(model) {
        const stmts = [];
        for (const [name, values] of Object.entries(model.enums)) {
            stmts.push(...this.printEnum(name, [...values]));
        }
        return stmts;
    }
    printCreateEntity(entity) {
        return [stmtCreateEntity({ entityName: entity.name })];
    }
    printColumn(context) {
        const dataType = context.column.type === Model.ColumnType.Enum ? context.column.columnType : ModelStmtPrinter.typesInverse[context.column.type];
        return [stmtAddSchemaColumn({
                entityName: context.entity.name,
                columnName: context.column.name,
                dataType,
                nullable: context.column.nullable,
            })];
    }
    printRelation(context) {
        const stmts = [];
        stmts.push(stmtAddRelation({
            owningEntity: context.entity.name,
            owningField: context.relation.name,
            inverseEntity: context.targetEntity.name,
            inverseField: context.targetRelation?.name,
            relationshipType: relationTypeMap[context.relation.type],
            nullable: 'nullable' in context.relation && context.relation.nullable,
        }));
        if ('joiningColumn' in context.relation) {
            stmts.push(stmtOnDelete({
                owningEntity: context.entity.name,
                owningField: context.relation.name,
                action: context.relation.joiningColumn.onDelete,
            }));
        }
        return stmts;
    }
    printEnum(enumName, values) {
        return [stmtAddEnumValue({ enumName, values })];
    }
}
Object.defineProperty(ModelStmtPrinter, "typesInverse", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: Object.fromEntries(Object.entries(dataTypesMapping).map(([a, b]) => [b, a]))
});
Object.defineProperty(ModelStmtPrinter, "onDeleteMappingInverse", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: Object.fromEntries(Object.entries(onDeleteMapping).map(([a, b]) => [b, a]))
});
export const printModelStmts = (schema) => {
    const printer = new ModelStmtPrinter();
    return printer.print(schema);
};
export const getPluginEntityNames = (schema) => {
    const pluginEntities = Object.values(schema.plugins).map(it => it.schema?.model.entities ?? {});
    return Array.from(new Set(pluginEntities.flatMap(it => Object.keys(it))));
};
export const printModel = (model, pluginEntityNames) => {
    if (!model || Object.keys(model?.entities ?? {}).length === 0) {
        return null;
    }
    let result = 'entities:\n';
    for (const entity of Object.values(model.entities)) {
        if (pluginEntityNames.includes(entity.name)) {
            continue;
        }
        result += ` ${entity.name}:\n`;
        result += entity.unique.length
            ? `  - unique: ${entity.unique.map(u => u.fields.join(' AND ')).join(', ')}\n`
            : '';
        result += '  - fields:\n';
        acceptEveryFieldVisitor(model, entity, {
            visitColumn(context) {
                if (context.column.name === context.entity.primary) {
                    return;
                }
                result += `   - ${context.column.name}:\n`;
                result += `      type: ${context.column.type.toLocaleLowerCase()}\n`;
                result += `      nullable: ${context.column.nullable}\n`;
                if (context.column.default) {
                    result += `      default: ${context.column.default}\n`;
                }
                if (context.column.type === Model.ColumnType.Enum) {
                    result += `      enum: ${context.column.columnType}\n`;
                }
            },
            visitRelation(context) {
                if (pluginEntityNames.includes(context.targetEntity.name)) {
                    result += `   - ${context.relation.name}:\n`;
                    result += '      type: plugin\n';
                }
                else {
                    result += `   - ${context.relation.name}:\n`;
                    result += `      type: ${context.relation.type}\n`;
                    result += `      target: ${context.targetEntity.name}.${context.targetRelation?.name}\n`;
                }
            },
        });
        result += '\n';
    }
    if (Object.keys(model.enums).length) {
        result += 'enums:\n';
        for (const [name, values] of Object.entries(model.enums)) {
            result += ` ${name}: [${values.join(', ')}]\n`;
        }
    }
    return result;
};
//# sourceMappingURL=printModel.js.map